import { required, useTranslate, type Validator } from "ra-core";

import { ArrayInput } from "@/components/admin/array-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { DateInput } from "@/components/admin/date-input";
import { NumberInput } from "@/components/admin/number-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";

import type { IncentiveProgramCode } from "../types";
import {
  validateNonNegativeNumber,
  validateTiers,
  validateValidTo,
} from "./incentiveProgramForm";

/**
 * Almikia Epic E7 (TASK-095): the typed parameter inputs for one incentive
 * program `code` (DATA CONTRACT E7's `parameters` shapes), plus its validity
 * dates -- shared between `IncentiveProgramEdit` (editing the CURRENT
 * version) and `NewVigenciaDialog` (drafting the NEXT version, prefilled
 * from the current one). Never renders an input for `best_bpo_team` /
 * `gasoline_insurance` -- their rules aren't defined yet (ticket
 * description), so their `parameters` stay the empty object the form
 * already carries.
 */
export const IncentiveProgramInputs = ({
  code,
  showValidTo = true,
  validateValidFrom = required(),
}: {
  code: IncentiveProgramCode;
  showValidTo?: boolean;
  /**
   * Overridden by `NewVigenciaDialog`: the new version's `valid_from` must
   * be strictly later than the current version's own end, not merely
   * present (see `validateNewValidFrom`).
   */
  validateValidFrom?: Validator;
}) => {
  const translate = useTranslate();

  return (
    <div className="flex flex-col gap-4">
      {code === "monthly_sale_incentive" ? (
        <>
          <NumberInput
            source="parameters.rate_pct"
            label="resources.incentive_programs.fields.rate_pct"
            step={0.01}
            validate={[required(), validateNonNegativeNumber]}
            helperText={false}
          />
          <NumberInput
            source="parameters.cap_mxn_per_sale"
            label="resources.incentive_programs.fields.cap_mxn_per_sale"
            step={1}
            validate={[required(), validateNonNegativeNumber]}
            helperText={false}
          />
          <TextInput
            source="parameters.min_level"
            label="resources.incentive_programs.fields.min_level"
            readOnly
            helperText={false}
          />
        </>
      ) : null}

      {code === "quarterly_outstanding_sales" ? (
        <>
          <NumberInput
            source="parameters.min_units"
            label="resources.incentive_programs.fields.min_units"
            step={1}
            validate={[required(), validateNonNegativeNumber]}
            helperText={false}
          />
          <ArrayInput
            source="parameters.tiers"
            label="resources.incentive_programs.fields.tiers"
            validate={validateTiers}
          >
            <SimpleFormIterator inline disableReordering>
              <NumberInput
                source="threshold_mxn"
                label="resources.incentive_programs.fields.tier_threshold_mxn"
                step={1}
                helperText={false}
              />
              <NumberInput
                source="rate_pct"
                label="resources.incentive_programs.fields.tier_rate_pct"
                step={0.01}
                helperText={false}
              />
              <BooleanInput
                source="inclusive"
                label="resources.incentive_programs.fields.tier_inclusive"
                defaultValue={false}
                helperText={false}
              />
            </SimpleFormIterator>
          </ArrayInput>
        </>
      ) : null}

      {code === "monthly_proactive_appointments" ? (
        <>
          <NumberInput
            source="parameters.target_appointments"
            label="resources.incentive_programs.fields.target_appointments"
            step={1}
            validate={[required(), validateNonNegativeNumber]}
            helperText={false}
          />
          <NumberInput
            source="parameters.reward_max_mxn"
            label="resources.incentive_programs.fields.reward_max_mxn"
            step={1}
            validate={[required(), validateNonNegativeNumber]}
            helperText={false}
          />
        </>
      ) : null}

      {code === "best_bpo_team" || code === "gasoline_insurance" ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.incentive_programs.no_parameters_yet")}
        </p>
      ) : null}

      <DateInput
        source="valid_from"
        label="resources.incentive_programs.fields.valid_from"
        validate={validateValidFrom}
        helperText={false}
      />
      {showValidTo ? (
        <DateInput
          source="valid_to"
          label="resources.incentive_programs.fields.valid_to"
          validate={validateValidTo}
          helperText={false}
        />
      ) : null}
    </div>
  );
};
